/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import React, { useEffect } from "react";
import {
  dispatch,
  PaymentStore,
  ScriptStatus,
  UserStore,
  useScript,
} from "@nf/common";
import dynamic from "next/dynamic";
const CreditCardIframe = dynamic<any>(() =>
  import("components/PaymentMethods").then((mod) => mod.CreditCardIframe)
);

import styles from "./SettingsPaymentDetails.module.scss";
import {
  useCleanupPaymentScript,
  useLanguage,
  useOnRouteChangeStart,
} from "../../../hooks";
import { usePaymentDetails } from "./hooks/usePaymentDetails";
import {
  ActionsHandlerWrapper,
  usePaymentDetailsContext,
  ActiveOfferContainer,
  ChangePaymentDetailsButton,
} from "./components";
import { NoSubscriptionsContainer } from "../NoSubscriptionContainer/NoSubscriptionsContainer";
import {
  ERROR_VALUE,
  PAYMENT_PARAM,
  SUCCESS_VALUE,
  useCreatePaymentCallback,
} from "../../../hooks/useCreatePaymentCallback";
import {
  UpdatePaymentStatusError,
  UpdatePaymentStatusSuccess,
} from "../../UpdatePaymentStatus";
import { useRouter } from "next/router";

export const SettingsPaymentDetails = () => {
  const { isFormSent, setApiErrors, apiErrors } = usePaymentDetailsContext();
  const { query } = useRouter();

  const { payment, isPaymentLoading, subscriptions, subscriptionError } =
    usePaymentDetails();

  const userHasSubscription = !!subscriptions?.length;

  const language = useLanguage();
  const scriptStatus = useScript(
    payment?.IframeUrl
      ? payment?.IframeUrl +
          `&handleRedirectInsideIFrame=false&language=${language}`
      : ""
  );

  const cleanUpPaymentFrameAndScript = useCleanupPaymentScript(
    payment?.IframeUrl
  );

  const resetPayment = () => {
    dispatch(PaymentStore.Actions.resetPayment());
    cleanUpPaymentFrameAndScript();
  };

  useOnRouteChangeStart(resetPayment);

  const { getUpdatePaymentCallbackUrl } = useCreatePaymentCallback();

  useEffect(() => {
    getUpdatePaymentCallbackUrl();
    dispatch(PaymentStore.Actions.getPaymentDetails());
    !userHasSubscription && dispatch(UserStore.Actions.getUserSubscriptions());
  }, []);

  useEffect(() => {
    if (isFormSent && subscriptionError) {
      setApiErrors({
        ...apiErrors,
        CouponError: [subscriptionError.Message as string],
      });
    }
  }, [subscriptionError, isFormSent]);

  const isPaymentSuccess = query[PAYMENT_PARAM] === SUCCESS_VALUE;
  const isPaymentFail = query[PAYMENT_PARAM] === ERROR_VALUE;
  const isScriptReady = scriptStatus !== ScriptStatus.ready;
  const displayPaymentButton =
    userHasSubscription && isScriptReady && !isPaymentSuccess;

  return (
    <div className={styles.Container}>
      <ActionsHandlerWrapper>
        {userHasSubscription ? (
          <ActiveOfferContainer scriptStatus={scriptStatus} />
        ) : (
          <NoSubscriptionsContainer />
        )}
      </ActionsHandlerWrapper>
      {isPaymentSuccess && <UpdatePaymentStatusSuccess />}
      {isPaymentFail && <UpdatePaymentStatusError />}
      {displayPaymentButton && <ChangePaymentDetailsButton />}
      <CreditCardIframe
        centered={false}
        scriptStatus={scriptStatus}
        isLoading={isPaymentLoading}
      />
    </div>
  );
};
